import { useCallback, useEffect } from "react";
import { ApiTaskKanban, ApiTaskKanbanStatus } from "src/api/types";
import useTenant from "src/hooks/api/services/tenants/useTenant";
import useTasksKanban from "src/hooks/api/tasks/useTasksKanban";
import useAuth from "src/hooks/selectors/useAuth";
import socket, { listen } from "src/lib/socket";

type SocketEvent = {
	type: "DELETE" | "UPDATE" | "CREATE";
	id: number;
	status: ApiTaskKanbanStatus;
	data?: any;
};

export default function useTasksKanbanListeners() {
	const auth = useAuth();
	const { tenant } = useTenant();
	const {
		state,
		api,
		actions: { getColumn, setStatus, setState },
	} = useTasksKanban();

	const urls = {
		status: `tasks.${auth?.company?.hid}.status`,
		update: `tasks.${auth?.company?.hid}.update`,
	};

	const onStatus = useCallback(
		(res: any) => {
			// if (process?.env?.NODE_ENV === "development") {
			// 	console.log("onStatus -> ", res);
			// }
			const ids = Object.values(state?.columns || {})
				.map((column) => column?.tasks || [])
				.reduce((acc: any, curr: any) => [...acc, ...curr], [])
				.map((item) => item.id);

			if (res?.from && res?.to && res?.id && res.to !== res.from) {
				if (ids.includes(res.id)) {
					setStatus(res);
				} else {
					getColumn(res.to);
				}
			}
		},
		[getColumn, setStatus, state]
	);

	const onUpdate = useCallback(
		async (event: SocketEvent) => {
			// if (process?.env?.NODE_ENV === "development") {
			// 	console.log("onUpdate -> ", event);
			// }
			const ids = Object.values(state?.columns || {})
				.map((column) => column?.tasks || [])
				.reduce((acc: any, curr: any) => [...acc, ...curr], [])
				.map((item) => item.id);

			if (event?.id) {
				if (event.type === "DELETE" && ids?.includes(event.id)) {
					const keys: ApiTaskKanbanStatus[] = Object.keys(
						state.columns
					) as any[];
					const status = keys.find((key: ApiTaskKanbanStatus) => {
						const exists = state.columns[key].tasks.find(
							(item: any) => item.id === event.id
						);
						return exists ? true : false;
					});
					if (status) {
						setState((state: any) => {
							const column = state.columns[status];

							column.tasks = column?.tasks?.filter(
								(item: ApiTaskKanban) => item.id !== event.id
							);
							return {
								...state,
								columns: {
									...state.columns,
									[status]: column,
								},
							};
						});
					}
				}

				if (event.type === "UPDATE" && ids?.includes(event.id)) {
					const keys: ApiTaskKanbanStatus[] = Object.keys(
						state.columns
					) as any[];

					//Find the status by the id
					const status = keys.find((key: ApiTaskKanbanStatus) => {
						const exists = state.columns[key].tasks.find(
							(item: any) => item.id === event.id
						);
						return exists ? true : false;
					});

					if (status) {
						//Get the new task values
						const { data } = await api
							.get(`/kanban/tasks/${event.id}`)
							.then(({ data }) => data);

						if (data?.id) {
							if (status && data.status !== status) {
								const oldStatus = status;
								const newStatus = data.status;
								console.log({
									old: oldStatus,
									new: newStatus,
								});
								setState((state: any) => {
									const old = state.columns[oldStatus];
									if (old.status !== "loading") {
										old.tasks = old.tasks.filter(
											(item: ApiTaskKanban) =>
												item.id !== event.id
										);
									}

									const updated = state.columns[newStatus];
									if (updated.status !== "loading") {
										updated.tasks = [
											data,
											...updated.tasks,
										];
									}
									return {
										...state,
										columns: {
											...state.columns,
											[oldStatus]: old,
											[newStatus]: updated,
										},
									};
								});
							} else {
								setState((state: any) => {
									const column = state.columns[data.status];
									column.tasks = column?.tasks?.map(
										(item: ApiTaskKanban) =>
											item.id === event.id ? data : item
									);
									return {
										...state,
										columns: {
											...state.columns,
											[data.status]: column,
										},
									};
								});
							}
						}
					}
				}
				if (event.type === "CREATE") {
					//Get the new task values
					const { data } = await api
						.get(`/kanban/tasks/${event.id}`)
						.then(({ data }) => data);
					const status = data.status;
					if (data?.id && status) {
						setState((state: any) => {
							const column = state.columns[status];
							column.tasks = [data, ...column?.tasks];
							return {
								...state,
								columns: {
									...state.columns,
									[status]: column,
								},
							};
						});
					}
				}
			}
		},
		[api, setState, state.columns]
	);

	useEffect(() => {
		if (tenant.modules.includes("intern-task")) {
			listen(urls.status, onStatus);
			listen(urls.update, onUpdate);
		}
		return () => {
			socket.off(urls.status, onStatus);
			socket.off(urls.update, onUpdate);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [state]);
}
